import React, {useMemo, useState, useRef, useLayoutEffect} from 'react'
import clsx from 'clsx'
import styles from './UIGameBank.module.scss'
import {BankState, CardEnum, CardStateOrdered, CardStateUnordered, CardTypeEnum, ActionState, TurnState} from '@colonist/game-states'
import {UIGameCardStack} from '../../common/UIGameCardStack/UIGameCardStack'
import {BaseCardData, CardDataController} from '@colonist/game-card-types'
import {CardValidatorUnordered, Point} from '@colonist/game-mechanic-validators'
import {ModeSettingEnum} from '@colonist/game-base-types'
import {TranslationObject} from '@colonist/i18n'
import {Tooltip} from '../../common/Tooltip/Tooltip'
import {T} from '../../common/T/T'
import {UIGameProgressCardTooltipBody} from '../../common/UIGameProgressCardTooltipBody/UIGameProgressCardTooltipBody'
import IconArrowPlayerProfile from '@static/images/icon_arrow_player_profile.svg'
import Bank from '@static/images/bank.svg'
import {CARD_TIP_BODY, CARD_TIP_TITLE} from '@colonist/ui-game'
import {UIGameFloatingText} from '../../common/UIGameFloatingText'
import {useGameStore} from '../../store/GameStoreContext'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
import {useIsReplay} from '../../store/selectors/useIsReplay'
import {getTranslationObjectForClickState} from './utils'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator'

const BANK_CARD_ORDER: CardEnum[] = [
    CardEnum.Lumber,
    CardEnum.Brick,
    CardEnum.Wool,
    CardEnum.Grain,
    CardEnum.Ore,
    CardEnum.Paper,
    CardEnum.Cloth,
    CardEnum.Coin,
]

type BackgroundColor = 'beige' | 'transparent'

interface UIGameBankCoreProps {
    bankState: BankState
    stackLimitModeSetting: ModeSettingEnum
    className?: string
    onClickCard?: (cardEnum: CardEnum) => void
    backgroundColor?: BackgroundColor
    isExpandOnHoverEnabled?: boolean
    isLandscape: boolean
    floatingText?: TranslationObject
}

export type UIGameBankProps =
  | (UIGameBankCoreProps & {
      developmentCards: CardStateOrdered
      progressCards?: never
  })
  | (UIGameBankCoreProps & {
      progressCards: CardStateOrdered
      developmentCards?: never
  })

interface CardEnumCountData {
    cardEnum: CardEnum
    count: number
    stackMax: number
    extraTooltipBody: TranslationObject | undefined
    cardData: BaseCardData | undefined
}

interface CardTypeStackLimits {
    resource: number[]
    commodity: number[]
    development: number[]
    progress: number[]
}

export function getStackSize(amountOfCards: number, stackLimits: number[]): number {
    if(amountOfCards == stackLimits[0]) return 0
    if(amountOfCards <= stackLimits[1]) return 1
    if(amountOfCards <= stackLimits[2]) return 2
    return 3
}

function getStackLimits(cardType: CardTypeEnum, cardStackLimits: CardTypeStackLimits): number[] {
    switch(cardType) {
        case CardTypeEnum.Resource: return cardStackLimits.resource
        case CardTypeEnum.Commodity: return cardStackLimits.commodity
        case CardTypeEnum.Development: return cardStackLimits.development
        case CardTypeEnum.ProgressTrade: return cardStackLimits.progress
        default: return []
    }
}

function getExtraTooltipBody(size: number, cardStackLimits: number[]): TranslationObject | undefined {
    if(size === 0) return {key: 'strings:game.tooltips.cardCountInStack.empty'}

    return {
        key: 'strings:game.tooltips.cardCountInStack.full',
        options: {lowerBound: cardStackLimits[size - 1] + 1, upperBound: cardStackLimits[size]},
    }
}

function getBankCardCountDatas(cardState: CardStateUnordered,
                               isExpanded: boolean,
                               commodities: CardEnum[],
                               cardStackLimits: CardTypeStackLimits): CardEnumCountData[] {
    const cardEnumCountDatas = Object.entries(cardState).map(([cardEnum, count]) => {
        const cardData = CardDataController.getCardDataForCard(Number(cardEnum))

        const stackLimits = cardData ? getStackLimits(cardData.cardTypeEnum, cardStackLimits) : []
        const stackSize = getStackSize(count, stackLimits)
        const extraTooltipBody = cardData ? getExtraTooltipBody(stackSize, stackLimits) : undefined

        return {
            cardEnum: Number(cardEnum),
            stackMax: stackSize,
            count: count,
            extraTooltipBody: extraTooltipBody,
            cardData: cardData ?? undefined,
        }
    })

    cardEnumCountDatas.sort((firstCard, secondCard) => {
        const firstCardIndex = BANK_CARD_ORDER.indexOf(firstCard.cardEnum)
        const secondCardIndex = BANK_CARD_ORDER.indexOf(secondCard.cardEnum)
        return firstCardIndex - secondCardIndex
    })

    if(isExpanded) return cardEnumCountDatas

    const cardEnumCountDatasWithoutCommodities = cardEnumCountDatas.filter(({cardEnum}) => !commodities.includes(cardEnum))
    return cardEnumCountDatasWithoutCommodities
}

function getDevelopmentOrProgressCardCountData(developmentCards: CardStateOrdered | undefined,
                                               progressCards: CardStateOrdered | undefined,
                                               cardStackLimits: CardTypeStackLimits): CardEnumCountData | undefined {
    if(!developmentCards && !progressCards) return undefined
    const isProgressCard = progressCards !== undefined

    const cards = isProgressCard ? progressCards.cards : developmentCards?.cards
    if(!cards) return undefined

    const cardEnum = isProgressCard ? CardEnum.ProgressCardBack : CardEnum.DevelopmentBack
    const cardData = CardDataController.getCardDataForCard(cardEnum)
    if(!cardData) return undefined

    const stackLimits = isProgressCard ? cardStackLimits.progress : cardStackLimits.development

    const stackSize = getStackSize(cards.length, stackLimits)
    const extraTooltipBody = isProgressCard ? undefined : getExtraTooltipBody(stackSize, stackLimits)

    return {
        cardEnum: cardEnum,
        stackMax: stackSize,
        count: cards.length,
        extraTooltipBody: extraTooltipBody,
        cardData: cardData,
    }
}

function getResourceCardStackLimits(modeSetting: ModeSettingEnum): number[] {
    switch(modeSetting) {
        case ModeSettingEnum.Classic4P:
        case ModeSettingEnum.Seafarers4P:
        case ModeSettingEnum.CitiesAndKnights4P:
        case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return [0, 7, 13, 19]
        case ModeSettingEnum.Classic56P:
        case ModeSettingEnum.Seafarers56P:
        case ModeSettingEnum.CitiesAndKnights56P:
        case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return [0, 8, 16, 24]
        case ModeSettingEnum.Classic78P: return [0, 10, 20, 29]
        default: return [0, 7, 13, 19]
    }
}

function getCommodityCardStackLimits(modeSetting: ModeSettingEnum): number[] {
    switch(modeSetting) {
        case ModeSettingEnum.Classic4P:
        case ModeSettingEnum.Seafarers4P:
        case ModeSettingEnum.CitiesAndKnights4P:
        case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return [0, 4, 8, 12]
        case ModeSettingEnum.Classic56P:
        case ModeSettingEnum.Seafarers56P:
        case ModeSettingEnum.CitiesAndKnights56P:
        case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return [0, 6, 12, 18]
        case ModeSettingEnum.Classic78P: return [0, 8, 16, 24]
        default: return [0, 4, 8, 12]
    }
}

function getDevelopmentCardStackLimits(modeSetting: ModeSettingEnum): number[] {
    switch(modeSetting) {
        case ModeSettingEnum.Classic4P:
        case ModeSettingEnum.Seafarers4P:
        case ModeSettingEnum.CitiesAndKnights4P:
        case ModeSettingEnum.CitiesAndKnightsSeafarers4P: return [0, 9, 17, 25]
        case ModeSettingEnum.Classic56P:
        case ModeSettingEnum.Seafarers56P:
        case ModeSettingEnum.CitiesAndKnights56P:
        case ModeSettingEnum.CitiesAndKnightsSeafarers56P: return [0, 12, 23, 34]
        case ModeSettingEnum.Classic78P: return [0, 15, 29, 43]
        default: return [0, 9, 17, 25]
    }
}

export function getPointPositionFromBank(cardEnum: CardEnum): Point | undefined {
    const cardData = CardDataController.getCardDataForCard(cardEnum)

    const bankIcon = document.querySelector('[data-bank-icon]') as HTMLElement | null
    const bankIconCenter = bankIcon ? bankIcon.getBoundingClientRect() : undefined

    const uiGameContainer = document.getElementById('ui-game') as HTMLElement | null
    if(uiGameContainer == undefined) return bankIconCenter

    const uiGameRect = uiGameContainer.getBoundingClientRect()
    const bankIconCenterInCanvas = bankIconCenter ? new Point(bankIconCenter.x - uiGameRect.left, bankIconCenter.y - uiGameRect.top) : undefined

    if(cardData == undefined) return bankIconCenterInCanvas

    let cardEnumToGetCenterOf: CardEnum
    switch(cardData.cardTypeEnum) {
        case CardTypeEnum.ProgressPolitics:
        case CardTypeEnum.ProgressScience:
        case CardTypeEnum.ProgressTrade: cardEnumToGetCenterOf = CardEnum.ProgressCardBack; break
        case CardTypeEnum.Development: cardEnumToGetCenterOf = CardEnum.DevelopmentBack; break
        default: cardEnumToGetCenterOf = cardEnum; break
    }
    const bankContainer = document.getElementById('bank-container') as HTMLElement | null
    if(bankContainer == undefined) return bankIconCenterInCanvas
    const cardElement = bankContainer.querySelector(`[data-card-enum="${cardEnumToGetCenterOf}"]`) as HTMLElement | null
    if(cardElement != undefined) {
        const cardElementCenter = cardElement.getBoundingClientRect()
        const cardElementCenterInCanvas = new Point(cardElementCenter.x - uiGameRect.left, cardElementCenter.y - uiGameRect.top)
        return cardElementCenterInCanvas
    }

    return bankIconCenterInCanvas
}

function getFloatingTextTranslationObject(isMyTurn: boolean | undefined,
                                          actionState: ActionState | undefined,
                                          turnState: TurnState | undefined,): TranslationObject | undefined {
    if(isMyTurn == undefined) return undefined
    if(actionState == undefined) return undefined
    if(turnState == undefined) return undefined
    return getTranslationObjectForClickState(isMyTurn, actionState, turnState)
}

export const UIGameBank = ({
    className,
    isLandscape,
}: {
    className?: string
    isLandscape: boolean
}) => {
    const bankState = useGameStore((state) => state.gameState.bankState)
    const developmentCards = useGameStore((state) => state.gameState.mechanicDevelopmentCardsState?.bankDevelopmentCards)
    const progressCards = useGameStore((state) => state.gameState.mechanicProgressCardsState?.bankProgressCards)
    const myColor = useGameStore((state) => state.myColor)
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const isReplay = useIsReplay()
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const wantedCards = useGameStore((state) => state.gameTrade.wantedCards)
    const setWantedCards = useGameStore((state) => state.gameTrade.setWantedCards)
    const setIsBankTradeMode = useGameStore((state) => state.gameTrade.setIsBankTradeMode)
    const isBankHidden = beginnerActionState?.hiddenUIElements.includes(HiddenUIElement.Bank)
    const isShowTradePanel = useGameStore((state) => state.gameTrade.isShowTradePanel)
    const setIsShowTradePanel = useGameStore((state) => state.gameTrade.setIsShowTradePanel)
    const currentStateValidator = useCurrentStateValidator()

    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P

    const isMyTurn = currentStateValidator.currentTurnPlayers.includes(myColor)
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const turnState = currentStateValidator.currentState.turnState

    const floatingText = getFloatingTextTranslationObject(isMyTurn, actionState, turnState)
    const showFloatingText = floatingText != undefined && !isReplay && !isBeginnerMode

    const onClickCard = (card: CardEnum) => {
        if(floatingText) return
        if(!isShowTradePanel) setIsShowTradePanel(true)
        setIsBankTradeMode(true)
        if(wantedCards.includes(CardEnum.AnyResource) && card === CardEnum.AnyResource) {
            return
        }
        const newWantedCards = [...wantedCards]
        newWantedCards.push(card)
        setWantedCards(newWantedCards)
    }
    const commonProps: UIGameBankCoreProps = {
        className: clsx(className, styles.responsiveContainer, {
            [styles.hidden]: isBankHidden,
            [styles.portrait]: !isLandscape,
        }),
        bankState: bankState,
        stackLimitModeSetting: modeSetting,
        onClickCard: onClickCard,
        isExpandOnHoverEnabled: isLandscape,
        backgroundColor: isLandscape ? 'beige' : 'transparent',
        isLandscape: isLandscape,
        ...(showFloatingText ? {floatingText: floatingText} : {}),
    }

    return (
        <>
            {progressCards ? (
                <UIGameBankComponent {...commonProps} progressCards={progressCards} />
            ) : developmentCards ? (
                <UIGameBankComponent {...commonProps} developmentCards={developmentCards} />
            ) : (
                <UIGameBankComponent {...commonProps} developmentCards={{cards: []}} />
            )}
        </>
    )
}

const UIGameBankComponent = ({
    bankState,
    className,
    developmentCards,
    stackLimitModeSetting,
    progressCards,
    onClickCard,
    backgroundColor = 'beige',
    isExpandOnHoverEnabled = true,
    isLandscape,
    floatingText,
}: UIGameBankProps) => {
    const hideBankCards = bankState.hideBankCards
    const containerRef = useRef<HTMLDivElement>(null)
    const [dynamicGap, setDynamicGap] = useState(-50)
    const {translationKeyContext} = useGameStore((state) => state.gameMode)
    const [isPointerOver, setIsPointerOver] = useState(false)

    const resourceCardStackLimits = getResourceCardStackLimits(stackLimitModeSetting)
    const commodityCardStackLimits = getCommodityCardStackLimits(stackLimitModeSetting)
    const developmentCardStackLimits = getDevelopmentCardStackLimits(stackLimitModeSetting)
    const cardStackLimits: CardTypeStackLimits = {
        resource: resourceCardStackLimits,
        commodity: commodityCardStackLimits,
        development: developmentCardStackLimits,
        progress: resourceCardStackLimits,
    }

    const commodities = useMemo(() => {
        const cardValidator = new CardValidatorUnordered(bankState.resourceCards)
        const commodityCards = cardValidator.getCardsOfType(CardTypeEnum.Commodity)

        return commodityCards
    }, [bankState.resourceCards])

    const canBankBeExpanded = commodities.length > 0
    const shouldForceBankToBeExpanded = !isExpandOnHoverEnabled && canBankBeExpanded
    const shouldExpandBasedOnPointerOver = isExpandOnHoverEnabled && isPointerOver
    const isExpanded = shouldForceBankToBeExpanded || shouldExpandBasedOnPointerOver

    const cardCountDatas = useMemo(() => {
        const resourceCountDatas = getBankCardCountDatas(bankState.resourceCards, isExpanded, commodities, cardStackLimits)
        const developmentOrProgressCardCountData = getDevelopmentOrProgressCardCountData(developmentCards, progressCards, cardStackLimits)

        const finalCardCountDatas =
            developmentOrProgressCardCountData ? [...resourceCountDatas, developmentOrProgressCardCountData] : resourceCountDatas

        return finalCardCountDatas
    }, [bankState.resourceCards, isExpanded, cardStackLimits, developmentCards, progressCards, dynamicGap])

    useLayoutEffect(() => {
        const bankElement = containerRef.current
        if(!bankElement) return

        const calculateGapBetweenCards = () => {
            const cardElement = bankElement.querySelector('[data-card-enum]')
            if(!cardElement) return
            const cardElementHeight = cardElement.getBoundingClientRect().height
            const gapPx = cardElementHeight * 0.9
            setDynamicGap(-gapPx)
        }

        calculateGapBetweenCards()

        const resizeObserver = new ResizeObserver(() => calculateGapBetweenCards())
        resizeObserver.observe(bankElement)

        return () => resizeObserver.disconnect()
    }, [])

    const shouldShowExpansionArrow = canBankBeExpanded && !isExpanded
    const shouldShowBankIcon = !isExpanded

    return (
        <div
            id="bank-container"
            className={clsx(styles.bank, styles[backgroundColor], className, {
                [styles.expanded]: isExpanded,
                [styles.portrait]: !isLandscape,
            })}
            onMouseEnter={canBankBeExpanded ? () => setIsPointerOver(true) : undefined}
            onMouseLeave={() => setIsPointerOver(false)}
            ref={containerRef}
        >
            {shouldShowExpansionArrow && (
                <img className={styles.expansionArrow} src={IconArrowPlayerProfile}/>
            )}

            {shouldShowBankIcon && (
                <img className={styles.bankIcon} data-bank-icon src={Bank}/>
            )}

            <div className={styles.cardRow}>
                {cardCountDatas.map(({cardEnum, count, stackMax, cardData, extraTooltipBody}) => {
                    const shouldShowExtraTooltip = (hideBankCards && extraTooltipBody !== undefined)
                    const isProgressCard = cardEnum === CardEnum.ProgressCardBack
                    const isOutOfCards = count === 0
                    const tipTitle: TranslationObject = {
                        ...CARD_TIP_TITLE[cardEnum],
                        options: {context: translationKeyContext},
                    }
                    const tipBody: TranslationObject = {
                        ...CARD_TIP_BODY[cardEnum],
                        options: {context: translationKeyContext},
                    }
                    const title = cardData ? <T id={tipTitle}/> : undefined
                    const body = isProgressCard && progressCards ? (
                        <UIGameProgressCardTooltipBody
                            hideBadge={hideBankCards}
                            progressCards={progressCards}
                            stackLimits={cardStackLimits.progress}
                            cardGap={dynamicGap}
                        />
                    ) : (
                        <>
                            {cardData && <T id={tipBody}/>}
                            {shouldShowExtraTooltip && (
                                <>
                                    <br />
                                    <br />
                                    <T id={extraTooltipBody}/>
                                </>
                            )}
                        </>
                    )

                    const doesOnClickErrorTextExist = floatingText != undefined
                    const isResourceOrCommodity = cardData?.cardTypeEnum == CardTypeEnum.Resource || cardData?.cardTypeEnum == CardTypeEnum.Commodity
                    const hasFloatingText = doesOnClickErrorTextExist && isLandscape
                    const canClickCard = !isOutOfCards && onClickCard != undefined && !hasFloatingText && isResourceOrCommodity && isLandscape

                    const handleClick = canClickCard ? () => onClickCard(cardEnum) : undefined

                    const cardStack = (
                        <UIGameCardStack
                            cardEnum={cardEnum}
                            className={clsx(styles.stack, {
                                [styles.outOfCardsTint]: isOutOfCards,
                                [styles.clickable]: canClickCard,
                            })}
                            count={isOutOfCards ? 1 : count}
                            data-card-enum={cardEnum}
                            gap={dynamicGap}
                            hideBadge={hideBankCards || isOutOfCards}
                            stackDirection="vertical"
                            stackMax={isOutOfCards ? 1 : stackMax}
                            size="sm"
                            {...(handleClick ? {onClick: handleClick} : {})}
                        />
                    )

                    return (
                        <Tooltip
                            centerText={isProgressCard}
                            content={body}
                            key={cardEnum}
                            position="bottom"
                            title={title}
                        >
                            {hasFloatingText ? (
                                <UIGameFloatingText
                                    direction="up"
                                    disabled={!hasFloatingText}
                                    text={floatingText}
                                >
                                    {cardStack}
                                </UIGameFloatingText>
                            ) : (
                                cardStack
                            )}
                        </Tooltip>
                    )
                },)}
            </div>
        </div>
    )
}
